// services/salaryService.ts

import api from "../config/api";
import type { ApiResponse } from "../types";

// ==================== TYPES ====================

export interface Salary {
  _id: string;
  userId: {
    _id: string;
    name: string;
    employeeId: string;
    role: string;
    baseSalary?: number;
    workDaysPerMonth?: number;
    dailyRate?: number;
    hourlyRate?: number;
  };
  year: number;
  month: number;
  baseSalary: number;
  workDaysPerMonth: number;
  dailyRate: number;
  hourlyRate: number;
  actualWorkDays: number;
  absentDays: number;
  totalWorkHours: number;
  lateDays: number;
  totalLateMinutes: number;
  salaryByHours: number;
  serviceBonus: number;
  specialBonus: number;
  deduction: number;
  totalSalary: number;
  notes?: string;
  status: 'draft' | 'approved' | 'paid';
  createdBy?: {
    _id: string;
    name: string;
  };
  approvedBy?: {
    _id: string;
    name: string;
  };
  approvedAt?: string;
  paidAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CalculateSalaryRequest {
  userId: string;
  year: number;
  month: number;
}

export interface CalculateBulkSalaryRequest {
  userIds: string[];
  year: number;
  month: number;
}

export interface CalculateAllSalaryRequest {
  year: number;
  month: number;
  roles?: string[];
}

export interface UpdateSalaryRequest {
  specialBonus?: number;
  notes?: string;
}

export interface BulkOperationRequest {
  salaryIds: string[];
}

export interface BulkOperationResult {
  success: Array<{
    userId?: string;
    salaryId?: string;
    userName?: string;
    employeeId?: string;
    salary: Salary;
  }>;
  failed: Array<{
    userId?: string;
    salaryId?: string;
    userName?: string;
    employeeId?: string;
    reason: string;
  }>;
  skipped?: Array<{
    userId: string;
    userName: string;
    employeeId: string;
    reason: string;
    salary: Salary;
  }>;
}

export interface BulkOperationResponse {
  summary: {
    total: number;
    success: number;
    failed: number;
    skipped?: number;
  };
  results: BulkOperationResult;
}

export interface GetSalariesParams {
  userId?: string;
  year?: number;
  month?: number;
  status?: string;
  page?: number;
  limit?: number;
}

// ==================== SALARY SERVICE ====================

export const salaryService = {
  /**
   * ຄຳນວນເງິນເດືອນ 1 ຄົນ
   */
  calculateSalary: async (
    data: CalculateSalaryRequest
  ): Promise<ApiResponse<Salary>> => {
    const response = await api.post('/salaries/calculate', data);
    return response.data;
  },

  /**
   * ຄຳນວນເງິນເດືອນຫຼາຍຄົນພ້ອມກັນ
   */
  calculateBulkSalary: async (
    data: CalculateBulkSalaryRequest
  ): Promise<ApiResponse<BulkOperationResponse>> => {
    const response = await api.post('/salaries/calculate-bulk', data);
    return response.data;
  },

  /**
   * ຄຳນວນເງິນເດືອນພະນັກງານທັງໝົດ
   */
  calculateAllSalary: async (
    data: CalculateAllSalaryRequest
  ): Promise<ApiResponse<BulkOperationResponse>> => {
    const response = await api.post('/salaries/calculate-all', data);
    return response.data;
  },

  /**
   * ດຶງລາຍການເງິນເດືອນ
   */
  getSalaries: async (
    params: GetSalariesParams = {}
  ): Promise<ApiResponse<{
    salaries: Salary[];
    pagination: {
      current: number;
      pages: number;
      total: number;
      limit: number;
    };
  }>> => {
    const response = await api.get('/salaries', { params });
    return response.data;
  },

  /**
   * ດຶງລາຍລະອຽດເງິນເດືອນ
   */
  getSalaryById: async (id: string): Promise<ApiResponse<Salary>> => {
    const response = await api.get(`/salaries/${id}`);
    return response.data;
  },

  /**
   * ແກ້ໄຂເງິນເດືອນ
   */
  updateSalary: async (
    id: string,
    data: UpdateSalaryRequest
  ): Promise<ApiResponse<Salary>> => {
    const response = await api.put(`/salaries/${id}`, data);
    return response.data;
  },

  /**
   * ອະນຸມັດເງິນເດືອນ
   */
  approveSalary: async (id: string): Promise<ApiResponse<Salary>> => {
    const response = await api.put(`/salaries/${id}/approve`);
    return response.data;
  },

  /**
   * ອະນຸມັດຫຼາຍລາຍການ
   */
  approveBulkSalary: async (
    data: BulkOperationRequest
  ): Promise<ApiResponse<BulkOperationResponse>> => {
    const response = await api.post('/salaries/approve-bulk', data);
    return response.data;
  },

  /**
   * ບັນທຶກວ່າຈ່າຍເງິນແລ້ວ
   */
  markAsPaid: async (id: string): Promise<ApiResponse<Salary>> => {
    const response = await api.put(`/salaries/${id}/pay`);
    return response.data;
  },

  /**
   * ບັນທຶກການຈ່າຍເງິນຫຼາຍລາຍການ
   */
  payBulkSalary: async (
    data: BulkOperationRequest
  ): Promise<ApiResponse<BulkOperationResponse>> => {
    const response = await api.post('/salaries/pay-bulk', data);
    return response.data;
  },

  /**
   * ລຶບເງິນເດືອນ
   */
  deleteSalary: async (id: string): Promise<ApiResponse<null>> => {
    const response = await api.delete(`/salaries/${id}`);
    return response.data;
  },

  /**
   * ເບິ່ງເງິນເດືອນຂອງຕົວເອງ
   */
  getMySalaries: async (params?: { 
    year?: number; 
    month?: number 
  }): Promise<ApiResponse<Salary[]>> => {
    const response = await api.get('/salaries/my-salaries', { params });
    return response.data;
  },

  // ==================== HELPER METHODS ====================

  /**
   * Format ສະຖານະເປັນພາສາລາວ
   */
  formatStatus: (status: Salary['status']): string => {
    const statusMap = {
      draft: 'ຮ່າງ',
      approved: 'ອະນຸມັດແລ້ວ',
      paid: 'ຈ່າຍແລ້ວ',
    };
    return statusMap[status] || status;
  },

  /**
   * ດຶງສີສະຖານະ
   */
  getStatusColor: (status: Salary['status']): string => {
    const colorMap = {
      draft: 'warning',
      approved: 'success',
      paid: 'processing',
    };
    return colorMap[status] || 'default';
  },

  /**
   * Format ເງິນ
   */
  formatCurrency: (amount: number): string => {
    return new Intl.NumberFormat('lo-LA').format(amount);
  },

  /**
   * Format ເດືອນ-ປີ
   */
  formatMonthYear: (year: number, month: number): string => {
    const months = [
      'ມັງກອນ', 'ກຸມພາ', 'ມີນາ', 'ເມສາ', 'ພຶດສະພາ', 'ມິຖຸນາ',
      'ກໍລະກົດ', 'ສິງຫາ', 'ກັນຍາ', 'ຕຸລາ', 'ພະຈິກ', 'ທັນວາ'
    ];
    return `${months[month - 1]} ${year}`;
  },

  /**
   * ດຶງລາຍຊື່ເດືອນ
   */
  getMonths: () => {
    return [
      { value: 1, label: 'ມັງກອນ' },
      { value: 2, label: 'ກຸມພາ' },
      { value: 3, label: 'ມີນາ' },
      { value: 4, label: 'ເມສາ' },
      { value: 5, label: 'ພຶດສະພາ' },
      { value: 6, label: 'ມິຖຸນາ' },
      { value: 7, label: 'ກໍລະກົດ' },
      { value: 8, label: 'ສິງຫາ' },
      { value: 9, label: 'ກັນຍາ' },
      { value: 10, label: 'ຕຸລາ' },
      { value: 11, label: 'ພະຈິກ' },
      { value: 12, label: 'ທັນວາ' },
    ];
  },

  /**
   * ດຶງລາຍຊື່ປີ (5 ປີຫຼ້າສຸດ)
   */
  getYears: () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let i = 0; i < 5; i++) {
      years.push({
        value: currentYear - i,
        label: `${currentYear - i}`,
      });
    }
    return years;
  },
};